import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import ReactModal from 'react-modal'
import { connect } from 'react-redux'
import ResponsiveImage from 'components/images/responsive-image'
import { withI18next } from 'lib/i18n/withI18next'
import ProductApi from 'lib/api/product'
import createQuickAddRequest, {
  isAvailableSize,
} from 'components/shared/product/quick-add/request'
import openQuickAddBag from 'components/shared/product/quick-add/open-bag'
import copy from './copy'
import styles from './quick-add.scss'

export const isAvailableOutfitSize = isAvailableSize

export const orderOutfitSizes = (sizes = []) => {
  const order = [
    'XXXS',
    'XXS',
    'XS',
    'S',
    'M',
    'L',
    'XL',
    'XXL',
    'XXXL',
    '4XL',
    '5XL',
  ]
  return sizes.every((size) => order.includes(size.size))
    ? [...sizes].sort((a, b) => order.indexOf(a.size) - order.indexOf(b.size))
    : sizes
}

const OutfitQuickAdd = ({
  product,
  options,
  addToBagAction,
  openBag,
  refData,
  t,
  country,
  language,
}) => {
  const [open, setOpen] = useState(false)
  const [selectedId, setSelectedId] = useState(null)
  const [details, setDetails] = useState(product)
  const [sizes, setSizes] = useState(() => orderOutfitSizes(options))
  const [status, setStatus] = useState('')
  const [unavailableIds, setUnavailableIds] = useState([])
  const request = useRef(null)
  const closeRef = useRef(null)
  const bagRequested = useRef(false)
  const busy = status === 'adding'
  const uncertain = status === 'uncertain'
  const selected = sizes?.find(
    (size) => size.id_product_attribute === selectedId,
  )
  if (!request.current) {
    request.current = createQuickAddRequest({
      productId: product.id_product,
      refData,
      getProduct: (id) =>
        ProductApi.getProduct({ product_id: id, shop: country, language }),
      addToBag: addToBagAction,
      onProduct: (fresh) => {
        setDetails(fresh)
        setSizes(orderOutfitSizes(fresh.attributes))
      },
      onState: (next) => {
        if (next.status === 'adding') closeRef.current?.focus()
        setStatus(
          { success: 'added', retry: 'error' }[next.status] || next.status,
        )
        if (next.status === 'unavailable') {
          setUnavailableIds((ids) => [...ids, next.size.id_product_attribute])
          setSelectedId(null)
        }
      },
    })
  }
  useEffect(() => () => request.current.dispose(), [])

  const available = (size) =>
    isAvailableOutfitSize(size) &&
    !unavailableIds.includes(size.id_product_attribute)
  const add = () => {
    if (available(selected)) request.current.add(selected)
  }

  const price = details.prices || {}
  let actionLabel = selected ? 'add' : 'select'
  if (status === 'added') actionLabel = 'added'
  if (busy) actionLabel = 'adding'
  const original = price.original_price_formatted || details.formatted_price
  const discounted =
    price.discounted_price_formatted || details.formatted_discounted_price
  const image = Array.isArray(details.images)
    ? details.images[0]?.full_path || details.images[0]?.path
    : details.images?.full_cover_image_path
  const appElement =
    typeof document !== 'undefined'
      ? document.getElementById('__next') || document.getElementById('root')
      : null

  return (
    <div className="outfit-quick-add">
      <style jsx>{styles}</style>
      <button
        type="button"
        className="outfit-trigger"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          setOpen(true)
        }}
      >
        {copy(t, language, 'choose')}
      </button>
      {!open && status === 'added' && (
        <span role="status">{copy(t, language, 'added')}</span>
      )}
      <ReactModal
        isOpen={open}
        appElement={appElement || undefined}
        ariaHideApp={!!appElement}
        contentLabel={`${copy(t, language, 'choose')}: ${details.name}`}
        className="pdp-outfit-dialog"
        overlayClassName="pdp-outfit-overlay"
        bodyOpenClassName="pdp-outfit-modal-open"
        onRequestClose={() => setOpen(false)}
        onAfterOpen={() => closeRef.current?.focus()}
        onAfterClose={() => {
          if (bagRequested.current) {
            bagRequested.current = false
            openBag()
          }
        }}
        shouldCloseOnOverlayClick
      >
        <div className="outfit-dialog-content">
          <button
            ref={closeRef}
            type="button"
            className="outfit-close"
            onClick={() => setOpen(false)}
          >
            {copy(t, language, 'close')}
          </button>
          <div className="outfit-product-context">
            <ResponsiveImage
              src={image || product.images.full_cover_image_path}
              alt={details.name}
              imgRatio={1.33}
            />
            <div>
              <p>{details.name}</p>
              <p>
                {discounted || original}{' '}
                {discounted && original && discounted !== original && (
                  <del>{original}</del>
                )}
              </p>
            </div>
          </div>
          <fieldset disabled={busy || uncertain}>
            <legend>{copy(t, language, 'choose')}</legend>
            <div className="outfit-sizes">
              {sizes?.map((size) => (
                <button
                  key={size.id_product_attribute}
                  type="button"
                  aria-pressed={selectedId === size.id_product_attribute}
                  disabled={!available(size)}
                  onClick={() => {
                    setSelectedId(size.id_product_attribute)
                    setStatus('')
                  }}
                >
                  {size.size}
                  {!available(size) && (
                    <small>{copy(t, language, 'soldOut')}</small>
                  )}
                </button>
              ))}
            </div>
          </fieldset>
          {status && (
            <p role={status === 'added' || busy ? 'status' : 'alert'}>
              {copy(t, language, status)}
            </p>
          )}
          {(uncertain || status === 'unavailable') && (
            <button
              type="button"
              className="outfit-check-bag"
              onClick={() => {
                bagRequested.current = true
                setOpen(false)
              }}
            >
              {copy(t, language, 'checkBag')}
            </button>
          )}
          <button
            type="button"
            className="outfit-add"
            disabled={
              busy || uncertain || !available(selected) || status === 'added'
            }
            onClick={add}
          >
            {copy(t, language, actionLabel)}
          </button>
        </div>
      </ReactModal>
    </div>
  )
}

OutfitQuickAdd.propTypes = {
  product: PropTypes.shape({
    id_product: PropTypes.number,
    name: PropTypes.string,
    images: PropTypes.shape({ full_cover_image_path: PropTypes.string }),
  }).isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  addToBagAction: PropTypes.func.isRequired,
  openBag: PropTypes.func.isRequired,
  refData: PropTypes.shape({}),
  t: PropTypes.func.isRequired,
  country: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
}
OutfitQuickAdd.defaultProps = { refData: null }
export { OutfitQuickAdd }

const ContextualOutfitQuickAdd = ({ product, requestContext, ...props }) => (
  <OutfitQuickAdd
    key={`${product.id_product}:${requestContext}`}
    product={product}
    {...props}
  />
)
ContextualOutfitQuickAdd.propTypes = {
  product: PropTypes.shape({ id_product: PropTypes.number }).isRequired,
  requestContext: PropTypes.string.isRequired,
}

export default connect(
  (state) => ({
    ...state.internationalization,
    requestContext: JSON.stringify([
      state.auth?.user?.id_customer,
      state.auth?.isGuestMode,
      state.internationalization,
    ]),
  }),
  { openBag: openQuickAddBag },
)(withI18next()(ContextualOutfitQuickAdd))
